import { CalendarOutlined, ClockCircleOutlined } from '@ant-design/icons';
import {
  Alert, DatePicker, Flex, Form, Select, Space, TimePicker, Typography,
} from 'antd';
import useFormInstance from 'antd/es/form/hooks/useFormInstance';
import dayjs from 'dayjs';
import useIsMobile from 'hooks/useIsMobile';
import React, {
  useCallback, useContext, useEffect, useMemo, useRef, useState,
} from 'react';
import http from 'utils/http';
import { formatTime } from 'utils/date';
import { captureException } from 'utils/errors';
import useFetchedData from 'hooks/useFetchedData';
import { EMPTY_OBJECT } from 'constants';
import RestaurantTime from '../../components/RestaurantTime';
import EditConfirmation from '../EditConfirmation';
import { StepsContext } from '../Steps/StepsProvider';

function applyTimeToDate(dateVal, timeSource) {
  if (!dateVal || !timeSource) {
    return null;
  }
  const time = dayjs(timeSource);
  if (!time.isValid()) {
    return null;
  }
  return dayjs(dateVal)
    .hour(time.hour())
    .minute(time.minute())
    .second(time.second())
    .millisecond(0);
}

function SchedulingInput({
  value, onChange, merchantId, isCatering,
}) {
  const [loading, setLoading] = useState(false);
  const [dates, setDates] = useState();
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState(null);
  const isMobile = useIsMobile();
  const [open, setOpen] = useState(false);
  const form = useFormInstance();
  const items = form.getFieldValue('items');
  const [tempDate, setTempDate] = useState(null);
  const [tempTime, setTempTime] = useState(null);
  const scheduledFor = Form.useWatch('scheduled_for');
  const {
    loading: stepLoading, lock, unlock,
  } = useContext(StepsContext);
  const formMerchant = form.getFieldValue('merchant');

  const [outOfRange, setOutOfRange] = useState(false);
  const [firstAvailableTime, setFirstAvailableTime] = useState(null);
  const [openingHours, setOpeningHours] = useState(null);
  const [openingHoursOpen, setOpeningHoursOpen] = useState(false);

  const { data: fetchedMerchant } = useFetchedData(formMerchant && `v1/restaurants/${formMerchant?.id}`, (res) => res?.data || EMPTY_OBJECT);

  const merchant = useMemo(() => ({
    ...formMerchant,
    ...fetchedMerchant,
  }), [formMerchant, fetchedMerchant]);

  /** Avoid re-applying the same first-slot default when availability refetches. */
  const lastAutoFirstSlotKeyRef = useRef('');
  /** Skip pushing local date/time to form right after syncing from form value. */
  const skipPushAfterFormSyncRef = useRef(false);

  useEffect(() => {
    if (value == null || value === '') {
      lastAutoFirstSlotKeyRef.current = '';
    }
  }, [value]);

  useEffect(() => {
    lastAutoFirstSlotKeyRef.current = '';
  }, [merchant?.id]);

  useEffect(() => {
    if (!merchant?.id) {
      return;
    }
    setOutOfRange(false);
    lock();
    http.get(`v1/restaurants/${merchant.id}/availability/?scheduled_datetime=${new Date(scheduledFor || Date.now()).toISOString()}`).then(({ data: { data } }) => {
      if (data.failed_check) {
        setOutOfRange(true);
      }
      setFirstAvailableTime(data.first_available_time);
      setOpeningHours(data.schedule);
    }).catch((error) => {
      captureException(error);
    }).finally(unlock);
  }, [scheduledFor, merchant?.id]);

  useEffect(() => {
    if (!isCatering || !merchant?.id || !firstAvailableTime || value) {
      return;
    }
    const key = `${merchant.id}|${firstAvailableTime}`;
    if (lastAutoFirstSlotKeyRef.current === key) {
      return;
    }
    lastAutoFirstSlotKeyRef.current = key;
    onChange(dayjs(firstAvailableTime).toISOString());
  }, [isCatering, merchant?.id, firstAvailableTime, value, onChange]);

  useEffect(() => {
    if (!scheduledFor) {
      setSelectedTime();
      setSelectedDate();
      return;
    }
    const parsed = dayjs(scheduledFor);
    if (!parsed.isValid()) return;
    if (isCatering) {
      skipPushAfterFormSyncRef.current = true;
      setSelectedDate(parsed);
      setSelectedTime(parsed);
    } else if (dates?.length) {
      const dateStr = parsed.format('YYYY-MM-DD');
      const dateIndex = dates.findIndex((d) => d.date === dateStr);
      if (dateIndex >= 0) {
        setSelectedDate(dateIndex);
        const { times } = dates[dateIndex];
        const allSlots = times?.flatMap((t) => t.slots || []) ?? [];
        const timeStr = parsed.format('HH:mm');
        const slotIndex = allSlots.findIndex((s) => String(s?.start) === timeStr);
        if (slotIndex >= 0) setSelectedTime(slotIndex);
      }
    }
  }, [scheduledFor, isCatering, dates]);

  const timeOptions = useMemo(() => {
    if (!merchantId || isCatering) return [];
    if (selectedDate === null || selectedDate === undefined) {
      return [];
    }
    const { times } = dates[selectedDate];
    return times
      .reduce((all, d) => [...all, ...d.slots], [])
      .map((s, index) => ({
        value: index,
        label: `${formatTime({ fromHour: s.start })} - ${formatTime({ fromHour: s.end })}`,
        time: s,
      }));
  }, [merchantId, isCatering, selectedDate, dates]);

  useEffect(() => {
    if (loading) return;

    if (isCatering) {
      if (skipPushAfterFormSyncRef.current) {
        skipPushAfterFormSyncRef.current = false;
        return;
      }
      if (selectedDate == null || selectedTime == null) {
        return;
      }
      const formattedScheduledData = `${dayjs(selectedDate).format('YYYY-MM-DD')}T${dayjs(selectedTime).format('HH:mm:ss')}`;
      const nextIso = dayjs(formattedScheduledData).toISOString();
      if (value == null || value === '' || !dayjs(nextIso).isSame(dayjs(value), 'minute')) {
        onChange(nextIso);
      }
      return;
    }

    if (selectedDate !== undefined && selectedDate !== null && selectedTime !== undefined && selectedTime !== null) {
      const formattedScheduledData = dates[selectedDate]?.date
        ? `${dates[selectedDate]?.date}T${timeOptions[selectedTime]?.time?.start}`
        : null;
      const nextIso = formattedScheduledData ? dayjs(formattedScheduledData).toISOString() : null;
      const sameAsForm = (() => {
        if (nextIso == null && (value == null || value === '')) return true;
        if (nextIso == null || value == null || value === '') return false;
        return dayjs(nextIso).isSame(dayjs(value), 'minute');
      })();
      if (!sameAsForm) {
        onChange(nextIso);
      }
    } else if (value != null && value !== '') {
      onChange(null);
    }
  }, [selectedDate, selectedTime, loading, isCatering, dates, timeOptions, onChange, value]);

  useEffect(() => {
    if (merchantId && !isCatering) {
      setLoading(true);
      http
        .post(`v1/restaurants/${merchantId}/schedule-times/`, {
          items: [],
        })
        .then(({ data: { data } }) => {
          setDates(
            Object.entries(data)
              .filter(([_, times]) => times.length > 0)
              .map(([date, times], index) => ({
                index,
                label: dayjs(date)?.format('ddd, DD MMM'),
                value: index,
                date,
                times,
              })),
          );
          setLoading(false);
        });
    }
  }, [merchantId, isCatering]);

  const mergeDateWithExistingTime = useCallback((dateVal) => {
    if (!dateVal) {
      return { date: null, time: null };
    }
    const timeSource = selectedTime ?? (value ? dayjs(value) : null);
    const mergedTime = applyTimeToDate(dateVal, timeSource);
    return { date: dateVal, time: mergedTime ?? selectedTime };
  }, [selectedTime, value]);

  const handleSelectDate = useCallback((dateVal) => {
    if (items?.length) {
      setTempDate(dateVal);
      setOpen(true);
    } else {
      const { date, time } = mergeDateWithExistingTime(dateVal);
      setSelectedDate(date);
      if (!date) {
        setSelectedTime(null);
      } else if (time) {
        setSelectedTime(time);
      }
    }
  }, [items?.length, mergeDateWithExistingTime]);

  const handleSelectTime = useCallback((timeVal) => {
    if (items?.length) {
      setTempTime(timeVal);
      setOpen(true);
    } else {
      setSelectedTime(timeVal);
    }
  }, [items?.length]);

  const onConfirmEdit = () => {
    const nextDate = tempDate ?? selectedDate;
    setSelectedDate(nextDate);
    if (tempTime != null) {
      setSelectedTime(tempTime);
    } else if (nextDate) {
      const mergedTime = applyTimeToDate(nextDate, selectedTime ?? (value ? dayjs(value) : null));
      if (mergedTime) {
        setSelectedTime(mergedTime);
      }
    }
    setTempDate();
    setTempTime();
  };
  return (
    <>
      {
        firstAvailableTime && (
          <Typography.Paragraph>
            First available scheduling time:
            <Typography.Text strong>{dayjs(firstAvailableTime).format('ddd, MMM D, h:mm A')}</Typography.Text>
          </Typography.Paragraph>
        )
      }
      {
        openingHours && (
          <Typography.Paragraph>
            <Typography.Text>{merchant.name}</Typography.Text>
            {' '}
            <Typography.Text>
              operates in the
              {' '}
              { merchant.parent.location_timezone }
              {' '}
              time zone. Please select a time based on the restaurant&apos;s local time.
            </Typography.Text>

            <Typography.Link onClick={() => setOpeningHoursOpen(true)}>View restaurant&apos;s hours</Typography.Link>
          </Typography.Paragraph>
        )
      }

      <Space size="large" direction={isMobile ? 'vertical' : 'horizontal'} className="w-full mt-2">
        {isCatering ? (
          <Space direction={isMobile ? 'vertical' : 'horizontal'} gap={16}>
            <Flex gap={8} align="center">
              <Typography.Text style={{ whiteSpace: 'nowrap' }}>Date:</Typography.Text>
              <DatePicker
                name="scheduled_for"
                value={selectedDate}
                onChange={handleSelectDate}
                disabledDate={(current) => current && current < dayjs().startOf('day')}
                disabled={stepLoading}
                needConfirm={false}
              />
            </Flex>
            <Flex gap={8} align="center">
              <Typography.Text style={{ whiteSpace: 'nowrap' }}>Time:</Typography.Text>
              <TimePicker
                name="scheduled_for"
                value={selectedTime}
                onChange={handleSelectTime}
                showSecond={false}
                minuteStep={15}
                use12Hours
                format="hh:mm A"
                disabled={stepLoading}
                needConfirm={false}
              />
            </Flex>
            {
              outOfRange && scheduledFor && (
                <Alert type="error" message="Warning: The restaurant is closed or lead time isn't met. You can still place your order, but inform the restaurant." />
              )
            }
          </Space>
        ) : (
          <Space direction={isMobile ? 'vertical' : 'horizontal'}>
            <Flex gap={8} align="center">
              <Typography.Text>Date:</Typography.Text>
              <Select
                disabled={loading || stepLoading}
                style={{ width: 150 }}
                className="row-right-margin"
                name="scheduled_for_date"
                suffixIcon={<CalendarOutlined />}
                loading={loading}
                value={selectedDate}
                onChange={handleSelectDate}
                options={dates}
                allowClear
              />
            </Flex>
            <Flex gap={8} align="center">
              <Typography.Text>Time:</Typography.Text>
              <Select
                disabled={loading || stepLoading}
                style={{ width: 200 }}
                name="scheduled_for"
                className="row-right-margin"
                suffixIcon={<ClockCircleOutlined />}
                loading={loading}
                value={selectedTime}
                onChange={handleSelectTime}
                options={timeOptions}
                allowClear
              />
            </Flex>

          </Space>
        )}
        <EditConfirmation open={open} setOpen={setOpen} onConfirmEdit={onConfirmEdit} />
      </Space>
      <RestaurantTime
        openingHours={openingHours}
        merchant={merchant}
        open={openingHoursOpen}
        firstAvailableTime={firstAvailableTime}
        onClose={() => setOpeningHoursOpen(false)}
      />
    </>
  );
}

export default SchedulingInput;
